import React, {useEffect, useState} from 'react'
import {Button} from 'react-bootstrap'
import {VacancyCreateRequest} from '../../types/vacancy/VacancyCreateRequest'
import vacansyService from '../../api/vacancy.api'
import applicationService from '../../api/application.api'
import {CKEditor} from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import ResultModal from '../modals/ResultModal'
import ResultModalContent from '../modals/ResultModalContent'
import {ROUTES} from '../../helpers/routeConsts'
import {getErrorMessage} from '../../helpers/errorMessage'
import {socket} from '../../App'
import {useStores} from '../../stores'
import {useDictionaries} from "./api/get-dictionaries";
import dictionaryService from "../../api/dictionary.api";
import {FilterVacancyCompany} from "../filter/filter-vacancy-company";
import {IDictionaryItem} from "../../types/IDictionaryItem";
import {twMerge} from "tailwind-merge";

type Props = {
    id: number | null
}

interface FieldProps {
    label: string;
    name: string;
    value: any;
    required?: boolean;
    onChange: (field: string, value: any) => void;
    className?: string
}


const VacancyCreateForm = (props: Props) => {

    const tempRequest: VacancyCreateRequest = localStorage.getItem('vacancyCreateRequest') ? JSON.parse(localStorage.getItem('vacancyCreateRequest')!) : new VacancyCreateRequest()
    const tempPriveleges: Set<number> = localStorage.getItem('vacancyCreateRequestPriveleges') ? new Set(JSON.parse(localStorage.getItem('vacancyCreateRequestPriveleges')!)) : new Set<number>()
    const tempPersonalQualities: Set<number> = localStorage.getItem('vacancyCreateRequestPersonalQualities') ? new Set(JSON.parse(localStorage.getItem('vacancyCreateRequestPersonalQualities')!)) : new Set<number>()

    const [formValues, setFormValues] = useState<VacancyCreateRequest>(tempRequest)
    const [selectedPriveleges, setSelectedPriveleges] = useState<Set<number>>(tempPriveleges)
    const [selectedPersonalQualities, setSelectedPersonalQualities] = useState<Set<number>>(tempPersonalQualities)
    const {authStore} = useStores()
    const vacancyCompanyId = authStore.user?.vacancyCompany || formValues.vacancyCompanyId || null

    const {dictionaries, setDictionaries} = useDictionaries(vacancyCompanyId)

    const [selectedTemplate, setSelectedTemplate] = useState<VacancyCreateRequest | null>(null)
    const [allTemplates, setAllTemplates] = useState<any[]>([])

    const handleSaveTempRequest = (request: VacancyCreateRequest) => {
        localStorage.setItem('vacancyCreateRequest', JSON.stringify(request))
    }

    const [showResult, setShowResult] = useState<boolean>(false)
    const [resultModalContent, setResultModalContent] = useState<React.ReactNode>(<></>)
    const [link, setLink] = useState<string | null>(null)

    const handleCloseResult = () => {
        setShowResult(false)
    }

    const handleShowResult = () => {
        setShowResult(true)
    }

    const handleChange = (field: string, value: any) => {
        setFormValues((prev) => ({...prev, [field]: value}));
        handleSaveTempRequest({...formValues, [field]: value});
        fetchAllTemplates(value || null)
    };


    const handleForm = (e: React.FormEvent<any>, isDraft = false) => {
        e.preventDefault()

        vacansyService.create({
            ...formValues,
            isDraft,
            applicationId: 0,
            englishLevelId: typeof formValues.englishLevelId === 'number' ? formValues.englishLevelId : 0,
            salary: String(formValues.salary).replaceAll(' ', ''),
            vacancyCompanyId: vacancyCompanyId
        }).then((response) => {
            if (response.status === 200) {
                socket.emit('chat message', JSON.stringify({
                    email: authStore.user?.email,
                    action: 'Создана вакансия на должность ' + formValues.position,
                    link: '/vacancy-info/' + response.data.id,
                    type: 'vacancy-create',
                    vacancyCompanyId: vacancyCompanyId
                }));


                setLink(ROUTES.VACANCY + "/open")
                handleShowResult()

                localStorage.setItem("applicationCreateRequest", JSON.stringify(new VacancyCreateRequest()))
                localStorage.setItem("applicationCreateRequestPriveleges", JSON.stringify([]))
                localStorage.setItem("applicationCreateRequestPersonalQualities", JSON.stringify([]))
                if (isDraft) {
                    return setResultModalContent(<ResultModalContent type="success"
                                                                     message={"Шаблон вакансии успешно создан"}/>)
                }
                setResultModalContent(<ResultModalContent type="success" message={"Вакансия успешно создана"}/>)
            }
        }).catch((e: any) => {
            setResultModalContent(<ResultModalContent type="error" message={getErrorMessage(e)}/>)
            handleShowResult()
        })
    }

    const fetchAllTemplates = async (vacancyCompanyId?: number | null) => {
        try {
            const response = await vacansyService.findAllTemplates(vacancyCompanyId);
            const {data} = response;

            setAllTemplates(data?.map((item: any) => ({
                id: item.id,
                name: item.position
            })));
        } catch (error) {
            console.error("Ошибка при получении шаблонов вакансий:", error);
        }
    };

    useEffect(() => {
        if (props.id) {
            applicationService.findForVacancy(props.id).then((response) => {
                setSelectedPriveleges(new Set(response.data.privilegeIds));
                localStorage.setItem('vacancyCreateRequestPriveleges', JSON.stringify(response.data.privilegeIds));
                localStorage.setItem('vacancyCreateRequestPersonalQualities', JSON.stringify(response.data.personalQualitiesIds));
                setSelectedPersonalQualities(new Set(response.data.personalQualitiesIds));
                dictionaryService.findAllByParentId('division', response.data.departmentId).then((response) => {
                    setDictionaries((prev) => {
                        return {
                            ...prev,
                            divisions: response.data
                        }
                    })
                })

                const updatedFormValues = {
                    ...formValues,
                    ...response.data,
                    salary: String(response.data.salary).replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, " "),
                    applicationId: props.id
                };

                setFormValues(updatedFormValues);
                handleSaveTempRequest(updatedFormValues);
            });
        }
        const vacancyCompanyId = formValues?.vacancyCompanyId || authStore.user?.vacancyCompany || null
        fetchAllTemplates(vacancyCompanyId)
    }, [props.id]);

    useEffect(() => {
        dictionaryService.findAllByParentId('division', formValues.departmentId).then((response) => {
            setDictionaries((prev) => {
                return {
                    ...prev,
                    divisions: response.data.map((item: IDictionaryItem) => item)
                }
            })
        })
    }, [formValues.departmentId])

    const handleLanguageChange = (index: number, field: any, value: number) => {
        setFormValues((prev) => {
            const updatedLanguages = prev.languages.map((item, i) =>
                i === index ? {...item, [field]: value} : item
            );

            handleSaveTempRequest({...prev, languages: updatedLanguages})
            return {...prev, languages: updatedLanguages};
        });
    };

    const handleRemoveLanguage = (index: number) => {
        setFormValues((prev) => ({
            ...prev,
            languages: prev.languages.filter((_, i) => i !== index),
        }));
    };

    const handleAddLanguage = () => {
        setFormValues((prev) => ({
            ...prev,
            languages: [...prev.languages, {languageId: 0, degreeId: 0}],
        }));
    };

    const handleSelectTemplate = async (key: string, value: any) => {
        setSelectedTemplate(value)
        if (!value) {
            setSelectedPriveleges(new Set())
            setSelectedPersonalQualities(new Set())
            return setFormValues(new VacancyCreateRequest())
        }
        const {data} = await vacansyService.findTemplateInfo(value)

        const isSelectedDivision = dictionaries.divisions?.find(item => item.id === data?.divisionId)
        
        setFormValues({
            ...data,
            divisionId: isSelectedDivision ? data.divisionId : null,
            vacancyCompanyId: formValues.vacancyCompanyId || vacancyCompanyId
        })
        setSelectedPriveleges(new Set(data.privilegeIds))
        setSelectedPersonalQualities(new Set(data.personalQualitiesIds))
    }

    return (
        <>
            <ResultModal
                show={showResult}
                link={link}
                title={"Создание вакансии"}
                handleClose={handleCloseResult}
                children={resultModalContent}
            />

            <form onSubmit={(e) => handleForm(e)} className="white-form-block mt-4 pt-4">
                <FilterVacancyCompany
                    selectedValue={formValues.vacancyCompanyId}
                    onValueChange={(name: string, value: string) => handleChange(name, +value)}
                    fieldName={'vacancyCompanyId'}
                    className={'mb-5 flex'}
                />

                <SelectField
                    label={"Шаблон"}
                    name={'template'}
                    value={selectedTemplate}
                    onChange={handleSelectTemplate}
                    options={allTemplates}
                />

                <TextAreaField
                    label="Должность"
                    name="position"
                    value={formValues.position}
                    required
                    onChange={handleChange}
                />

                <div className="form-group mb-5 d-flex">
                    <label><span className="text-danger h5">*</span> Количество работников</label>

                    <input type="number" className="form-control flex-grow-1"
                           placeholder=''
                           required={true}
                           value={formValues.amountOfWorkers}
                           min={1}
                           onChange={(e) => {
                               setFormValues((prev) => ({
                                   ...prev,
                                   amountOfWorkers: e.target.value
                               }))
                               handleSaveTempRequest({
                                   ...formValues,
                                   amountOfWorkers: e.target.value
                               })
                           }}/>
                </div>

                <div className="form-group mb-5 d-flex">
                    <label><span className="text-danger h5">*</span> Зарплата</label>
                    <div className="d-flex align-items-center" style={{gap: 20}}>
                        <input type="text" className="form-control flex-grow-1"
                               placeholder=''
                               required={true}
                               value={formValues.salary}
                               onChange={(e) => {
                                   setFormValues((prev) => ({
                                       ...prev,
                                       salary: e.target.value.replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, " ")
                                   }))
                                   handleSaveTempRequest({
                                       ...formValues,
                                       salary: e.target.value.replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, " ")
                                   })
                               }}/>


                        <div className="form-check flex-grow-1">
                            <input className="form-check-input" type="checkbox"
                                   checked={formValues.afterTax}
                                   onChange={(e) => {
                                       setFormValues((prev) => ({
                                           ...prev,
                                           afterTax: e.target.checked
                                       }))
                                       handleSaveTempRequest({
                                           ...formValues,
                                           afterTax: e.target.checked
                                       })
                                   }}
                                   id="isAfterTax"/>
                            <label className="form-check-label" htmlFor="isAfterTax">
                                После вычета налогов
                            </label>
                        </div>
                    </div>
                </div>

                <div className="form-group mb-5 d-flex">
                    <label><span className="text-danger h5">*</span> Бонусы</label>
                    <div className="d-flex align-items-center flex-grow-1" style={{gap: 20}}>
                        <select value={formValues.bonusTypeId}
                                className='form-select'
                                style={{maxWidth: 200}}
                                required={true}
                                onChange={(e) => {
                                    setFormValues((prev) => ({...prev, bonusTypeId: +e.target.value}))
                                    handleSaveTempRequest({
                                        ...formValues,
                                        bonusTypeId: +e.target.value
                                    })
                                }}>
                            <option value="">-Выберите тип бонусов-</option>
                            {
                                dictionaries.bonusTypes.map((bonusType) => {
                                    return <option key={bonusType.id} value={bonusType.id}>{bonusType.name}</option>
                                })
                            }
                        </select>
                        <input type='text' className='form-control flex-grow-1'
                               value={formValues.bonusValue}
                               placeholder='размер бонуса или описание бонуса'
                               onChange={(e) => {
                                   setFormValues((prev) => ({...prev, bonusValue: e.target.value}))
                                   handleSaveTempRequest({
                                       ...formValues,
                                       bonusValue: e.target.value
                                   })
                               }}/>
                    </div>
                </div>


                <SelectField
                    label="Причина найма"
                    name="reasonForHiringId"
                    value={formValues.reasonForHiringId}
                    required
                    options={dictionaries.reasonForHiring}
                    onChange={(field, value) => handleChange(field, +value)}
                />

                <SelectField
                    label="Вид договора"
                    name="workContractTypeId"
                    value={formValues.workContractTypeId}
                    required
                    options={dictionaries.workContractType}
                    onChange={(field, value) => handleChange(field, +value)}
                />

                <CKEditorField
                    label="Описание компании для позиции"
                    name="aboutCompanyForVacancy"
                    value={formValues.aboutCompanyForVacancy}
                    onChange={handleChange}
                />

                <CKEditorField
                    label="Проблемы ради которых создается вакансия"
                    name="problems"
                    value={formValues.problems}
                    onChange={handleChange}
                />

                <TextAreaField
                    label="Обязанности"
                    name="responsibilities"
                    value={formValues.responsibilities}
                    required
                    onChange={handleChange}
                />

                <TextAreaField
                    label="Требования"
                    name="requirements"
                    value={formValues.requirements}
                    required
                    onChange={handleChange}
                />

                <CKEditorField
                    label="Ожидания"
                    name="expectations"
                    value={formValues.expectations}
                    onChange={handleChange}
                />

                <TextAreaField
                    label="Будет плюсом"
                    name="someWishes"
                    value={formValues.someWishes}
                    required
                    onChange={handleChange}
                />

                <TextAreaField
                    label="Предложения от нас"
                    name="whatWeOffer"
                    value={formValues.whatWeOffer}
                    required
                    onChange={handleChange}
                />

                <TextAreaField
                    label="Карьерные возможности"
                    name="opportunities"
                    value={formValues.opportunities}
                    required
                    onChange={handleChange}
                />


                <div className="form-group d-flex mb-5">
                    <label><span className="text-danger h5">*</span> Департамент</label>
                    <select className="form-select" id="groupSelect"
                            value={formValues.departmentId}
                            required={true}
                            onChange={(e) => {
                                setFormValues((prev) => ({
                                    ...prev,
                                    departmentId: +e.target.value
                                }))
                                handleSaveTempRequest({
                                    ...formValues,
                                    departmentId: +e.target.value
                                })
                            }}
                            aria-label="group label select example">
                        <option value="">-Выберите-</option>
                        {
                            dictionaries.department.map(department => (
                                <option key={department.id} value={department.id}>{department.name}</option>
                            ))
                        }
                    </select>

                </div>

                <div className="form-group d-flex mb-5">
                    <label>Отдел</label>
                    <select className="form-select" id="groupSelect"
                            value={formValues.divisionId ?? ''}
                            onChange={(e) => {
                                setFormValues((prev) => ({
                                    ...prev,
                                    divisionId: +e.target.value
                                }))
                                handleSaveTempRequest({
                                    ...formValues,
                                    divisionId: +e.target.value
                                })
                            }}
                            aria-label="group label select example">
                        <option value="">-Выберите-</option>
                        {
                            dictionaries.divisions.map(d => (
                                <option key={d.id} value={d.id}>{d.name}</option>
                            ))
                        }
                    </select>

                </div>


                <div className="form-group d-flex mb-5">
                    <label><span className="text-danger h5">*</span> График работы</label>
                    <select className="form-select" id="groupSelect"
                            value={formValues.workScheduleTypeId}
                            required={true}
                            onChange={(e) => {
                                setFormValues((prev) => ({
                                    ...prev,
                                    workScheduleTypeId: +e.target.value
                                }))
                                handleSaveTempRequest({
                                    ...formValues,
                                    workScheduleTypeId: +e.target.value
                                })
                            }}
                            aria-label="group label select example">
                        <option value="">-Выберите-</option>
                        {
                            dictionaries.workScheduleType.map(w => (
                                <option key={w.id} value={w.id}>{w.name}</option>
                            ))
                        }
                    </select>

                </div>

                <div className="form-group d-flex mb-5">
                    <label><span className="text-danger h5">*</span> Опыт работы</label>
                    <select className="form-select" id="groupSelect"
                            value={formValues.workExperienceTypeId}
                            required={true}
                            onChange={(e) => {
                                setFormValues((prev) => ({
                                    ...prev,
                                    workExperienceTypeId: +e.target.value
                                }))
                                handleSaveTempRequest({
                                    ...formValues,
                                    workScheduleTypeId: +e.target.value
                                })
                            }}
                            aria-label="group label select example">
                        <option value="">-Выберите-</option>
                        {
                            dictionaries.workExperienceType.map(w => (
                                <option key={w.id} value={w.id}>{w.name}</option>
                            ))
                        }
                    </select>

                </div>

                <div className="form-group d-flex mb-5">
                    <label>Уровень английского</label>
                    <select className="form-select" id="groupSelect"
                            value={formValues.englishLevelId}
                            onChange={(e) => {
                                setFormValues((prev) => ({
                                    ...prev,
                                    englishLevelId: +e.target.value
                                }))
                                handleSaveTempRequest({
                                    ...formValues,
                                    englishLevelId: +e.target.value
                                })
                            }}
                            aria-label="group label select example">
                        <option value="">-Выберите-</option>
                        {
                            dictionaries.englishLevel.map(el => (
                                <option key={el.id} value={el.id}>{el.name}</option>
                            ))
                        }
                    </select>

                </div>

                <div className="form-group d-flex mb-5">
                    <label>Другие языки</label>
                    <ul className="list-group" id="languages">
                        {formValues.languages.map((item, index) => (
                            <li key={index} className="list-group-item d-flex">
                                <div className="d-flex" style={{gap: 10}}>
                                    <select
                                        className="form-select"
                                        value={item.languageId}
                                        onChange={(e) => handleLanguageChange(index, "languageId", +e.target.value)}
                                        style={{width: 150}}
                                        required
                                    >
                                        <option value="">-Выберите язык-</option>
                                        {dictionaries.languages.map((language) => (
                                            <option key={language.id} value={language.id}>
                                                {language.name}
                                            </option>
                                        ))}
                                    </select>

                                    <select
                                        className="form-select"
                                        value={item.degreeId}
                                        onChange={(e) => handleLanguageChange(index, "degreeId", +e.target.value)}
                                        required
                                    >
                                        <option value="">-Выберите уровень-</option>
                                        {dictionaries.languageDegrees.map((degree) => (
                                            <option key={degree.id} value={degree.id}>
                                                {degree.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <Button variant="outline-secondary" className="ms-2" size="sm"
                                        onClick={() => handleRemoveLanguage(index)}>
                                    <i className="fa-solid fa-xmark"></i>
                                </Button>
                            </li>
                        ))}

                        <li className="list-group-item list-group-item-action" style={{cursor: "pointer"}}
                            onClick={handleAddLanguage}>
                            + Добавить язык
                        </li>
                    </ul>
                </div>

                <div className="form-group d-flex mb-5">
                    <label><span className="text-danger h5">*</span> Образование</label>
                    <select className="form-select" id="groupSelect"
                            value={formValues.educationDegreeId}
                            required={true}
                            onChange={(e) => {
                                setFormValues((prev) => ({
                                    ...prev,
                                    educationDegreeId: +e.target.value
                                }))
                                handleSaveTempRequest({
                                    ...formValues,
                                    educationDegreeId: +e.target.value
                                })
                            }}
                            aria-label="group label select example">
                        <option value="">-Выберите-</option>
                        {
                            dictionaries.educationDegree.map(ed => (
                                <option key={ed.id} value={ed.id}>{ed.name}</option>
                            ))
                        }
                    </select>

                </div>

                <div className="form-group mb-5 d-flex">
                    <label className='form-label'><span className="text-danger h5">*</span> Льготы</label>
                    <ul className='list-group flex-grow-1'>
                        {
                            dictionaries.privilege.map((item, index) => {
                                return <li className='list-group-item' key={index}>
                                    <div className="form-check">
                                        <input className="form-check-input" type="checkbox" value=""
                                               id={"priv-" + item.id}
                                               checked={selectedPriveleges.has(item.id)}
                                               onChange={(e) => {

                                                   setFormValues((prev) => {
                                                       const newSet = new Set(prev.privilegeIds);
                                                       if (e.target.checked) {
                                                           newSet.add(item.id)
                                                       } else {
                                                           newSet.delete(item.id)
                                                       }
                                                       setSelectedPriveleges(newSet)
                                                       localStorage.setItem('vacancyCreateRequestPriveleges', JSON.stringify(Array.from(newSet)))
                                                       return {
                                                           ...prev,
                                                           privilegeIds: Array.from(newSet)
                                                       }
                                                   })

                                                   const newSet = new Set(selectedPriveleges);
                                                   if (e.target.checked) {
                                                       newSet.add(item.id)
                                                   } else {
                                                       newSet.delete(item.id)
                                                   }
                                                   handleSaveTempRequest({
                                                       ...formValues,
                                                       privilegeIds: Array.from(newSet)
                                                   })

                                               }}
                                        />
                                        <label className="form-check-label" htmlFor={"priv-" + item.id}>
                                            {item.name}
                                        </label>
                                    </div>
                                </li>
                            })
                        }
                    </ul>
                </div>

                <div className="form-group mb-5 d-flex">
                    <label className='form-label'><span className="text-danger h5">*</span> Личные качества</label>
                    <ul className='list-group flex-grow-1'>
                        {
                            dictionaries.personalQuality.map((item, index) => {
                                return <li className='list-group-item' key={index}>
                                    <div className="form-check">
                                        <input className="form-check-input" type="checkbox" value=""
                                               id={"priv-" + item.id}
                                               checked={selectedPersonalQualities.has(item.id)}
                                               onChange={(e) => {

                                                   setFormValues((prev) => {
                                                       const newSet = new Set(prev.personalQualitiesIds);
                                                       if (e.target.checked) {
                                                           newSet.add(item.id)
                                                       } else {
                                                           newSet.delete(item.id)
                                                       }
                                                       setSelectedPersonalQualities(newSet)
                                                       localStorage.setItem('vacancyCreateRequestPersonalQualities', JSON.stringify(Array.from(newSet)))
                                                       return {
                                                           ...prev,
                                                           personalQualitiesIds: Array.from(newSet)
                                                       }
                                                   })

                                                   let newSet = new Set(formValues.personalQualitiesIds);
                                                   if (e.target.checked) {
                                                       newSet.add(item.id)
                                                   } else {
                                                       newSet.delete(item.id)
                                                   }
                                                   handleSaveTempRequest({
                                                       ...formValues,
                                                       personalQualitiesIds: Array.from(newSet)
                                                   })

                                               }}
                                        />
                                        <label className="form-check-label" htmlFor={"priv-" + item.id}>
                                            {item.name}
                                        </label>
                                    </div>
                                </li>
                            })
                        }
                    </ul>
                </div>

                <div className="form-group mb-5 d-flex">
                    <label>Дополнительные навыки</label>
                    <textarea className="form-control"
                              rows={3}
                              placeholder=''
                              value={formValues.additionalSkills}
                              onChange={(e) => {
                                  setFormValues((prev) => ({
                                      ...prev,
                                      additionalSkills: e.target.value
                                  }))
                                  handleSaveTempRequest({
                                      ...formValues,
                                      additionalSkills: e.target.value
                                  })
                              }}></textarea>
                </div>

                <div className="d-flex mb-5 gap-2">
                    <label htmlFor=""></label>
                    <Button type="submit" size={'lg'}>Создать вакансию</Button>
                    <Button type="button" onClick={(e) => handleForm(e, true)} size={'lg'}>Создать шаблон</Button>
                    <Button type="button" onClick={() => {
                        setFormValues(new VacancyCreateRequest())
                        setSelectedPriveleges(new Set())
                        setSelectedPersonalQualities(new Set())
                    }} variant='secondary' size={'lg'}>Сбросить</Button>
                </div>
            </form>
        </>
    )
}

export default VacancyCreateForm

export const TextAreaField: React.FC<FieldProps> = ({
                                                        label,
                                                        required = false,
                                                        value,
                                                        name,
                                                        onChange
                                                    }) => (
    <div className="form-group mb-5 d-flex">
        <label>
            {required && <span className="text-danger h5">*</span>} {label}
        </label>
        <div className="flex-grow-1">
      <textarea
          className="form-control"
          required={required}
          value={value}
          rows={6}
          onChange={(e) => onChange(name, e.target.value)}
      />
        </div>
    </div>
);

export const CKEditorField: React.FC<FieldProps> = ({
                                                        label,
                                                        value,
                                                        name,
                                                        onChange
                                                    }) => (
    <div className="form-group mb-5 d-flex">
        <label>{label}</label>
        <div className="flex-grow-1" style={{minHeight: 300}}>
            <CKEditor
                editor={ClassicEditor}
                data={value}
                onChange={(event, editor) => onChange(name, editor.getData())}
            />
        </div>
    </div>
);

export const InputField: React.FC<FieldProps & { type?: string; min?: number }> = (
    {
        label,
        required = false,
        value,
        name,
        onChange,
        type = "text",
        min
    }
) => (
    <div className="form-group mb-5 d-flex">
        <label>
            {required && <span className="text-danger h5">*</span>} {label}
        </label>
        <input
            type={type}
            className="form-control flex-grow-1"
            required={required}
            value={value}
            min={min}
            onChange={(e) =>
                onChange(name, type === "number" ? +e.target.value : e.target.value)
            }
        />
    </div>
);

export const SelectField: React.FC<FieldProps & { options: { id: number; name: string }[] }> = (
    {
        label,
        required = false,
        value,
        name,
        onChange,
        options,
        className
    }
) => (
    <div className={twMerge('form-group mb-5 d-flex', className)}>
        <label>
            {required && <span className="text-danger h5">*</span>} {label}
        </label>
        <select
            className="form-select"
            value={value}
            required={required}
            onChange={(e) => onChange(name, +e.target.value)}
        >
            <option value="">-Выберите-</option>
            {options?.map((option) => (
                <option key={option.id} value={option.id}>
                    {option.name}
                </option>
            ))}
        </select>
    </div>
);

